import React from 'react'
import { DefaultOptionType } from 'antd/es/select'

import { ReservationShowEntity } from '@/api'
import { KBForm, KBIcon, KBTooltip } from '@/components/atoms'
import { getUserSelectOptions } from '@/components/organisms/KBUserSelect/KBUserSelect'
import { KBResourceReservationFormProps } from '@/components/molecules/KBResourceReservationForm/KBResourceReservationForm'
import { KBUserSelect } from '@/components/organisms'
import { t } from 'i18next'

interface AttendeesSelectProps {
  value?: number[]
  resourcesList: KBResourceReservationFormProps['resourcesList']
  reservationInfo?: ReservationShowEntity
  errorsMessages?: string
  onChange?: (value: (number | string)[]) => void
}
/**
 * 选择参会人组件
 */
const AttendeesSelect: React.FC<AttendeesSelectProps> = (props) => {
  const { value, onChange, resourcesList, reservationInfo, errorsMessages } =
    props

  const showAttendees = useMemo(() => {
    return (resourcesList || []).some((i) => {
      const rules = i.reservation_reserve_rule?.rules
      const isShareAble = i.resource_type.usage_mode === 'shareable'
      return (
        (rules?.external_attendees || rules?.internal_attendees) && isShareAble
      )
    })
  }, [resourcesList])

  const includes = useMemo(() => {
    const result: ('internal' | 'external')[] = []
    if (
      (resourcesList || []).some(
        (i) => i.reservation_reserve_rule?.rules?.internal_attendees
      )
    ) {
      result.push('internal')
    }
    if (
      (resourcesList || []).some(
        (i) => i.reservation_reserve_rule?.rules?.external_attendees
      )
    ) {
      result.push('external')
    }
    return result
  }, [resourcesList])

  // 编辑回填兜底: 外部联系人 user_book 按 host_user_id 私有隔离, 非创建者(如管理员、
  // 同名其它账号)编辑时 KBUserSelect 远程查不到, 回填会退化成裸 id(如 -1626)。
  // 这里用预定自带的 attendee 信息(含 user / user_book 的 email、name)构造初始 options,
  // value 编码与表单回填一致: 内部=user_id, 外部=0 - user_book_id。
  const initialOptions = useMemo<DefaultOptionType[]>(() => {
    const attendees = (reservationInfo?.attendees || []).filter(
      (i) => i.role !== 'host'
    )
    const fallbackUsers = attendees
      .map((i) => {
        if (i.is_external) {
          if (!i.user_book_id) return null
          // user.id 设为负值, 与 value(0 - user_book_id) 一致, 保证 KBUserSelect 内部
          // 按 option.value / option.user.id 的匹配自洽。
          return {
            id: 0 - i.user_book_id,
            name: i.user_book?.name || i.user_book?.email,
            email: i.user_book?.email,
          }
        }
        if (!i.user_id) return null
        return {
          id: i.user_id,
          name: i.user?.name,
          email: i.user?.email,
          avatar: i.user?.avatar,
        }
      })
      .filter((u): u is NonNullable<typeof u> => u !== null)
    return getUserSelectOptions(fallbackUsers as never, 'tags')
  }, [reservationInfo?.attendees])

  return showAttendees ? (
    <KBForm.Item
      name="userIds"
      label={
        <KBTooltip title={t('common.attendees')}>
          <KBIcon name="users" size={24} />
        </KBTooltip>
      }
      validateStatus={Boolean(errorsMessages?.length) ? 'error' : undefined}
      help={errorsMessages}
    >
      <KBUserSelect
        mode="tags"
        includes={includes.length === 2 ? 'internal,external' : includes[0]}
        className="tw-w-full"
        value={value}
        options={initialOptions}
        onChange={onChange}
        placeholder={t('common.attendsSelectPlaceHolder')}
        // TODO暂时先取消缓存，后续应该改成创建预定后，如果有添加了外部参会人才刷新缓存
        userBookStaleTime={1}
      />
    </KBForm.Item>
  ) : null
}

export default React.memo(AttendeesSelect)
